import * as v from 'valibot';

/**
 * Creates a schema that validates and reports issues while providing a fallback value.
 * Unlike v.fallback(), this preserves validation issues in the result while maintaining required fields.
 *
 * CAUTION — two known limitations (verified against valibot v1):
 * 1. The fallback value is NOT actually applied: `v.rawCheck` ignores the
 *    callback's return value, so the parse output keeps the original value.
 *    (Harmless today because validateAndTransformResponse returns raw data.)
 * 2. Inner issues of the wrapped schema are NOT suppressed: any nested
 *    message without a `@WARNING:` prefix stays ERROR-severity and will
 *    still drop the enclosing array item in validateAndTransformResponse.
 *    Fixing this requires `v.rawTransform` or a custom schema.
 *
 * @param schema - The schema to validate against
 * @param fallback - The fallback value to use when validation fails
 * @param message - Optional custom error message (supports @WARNING:/@ERROR: prefix)
 * @returns A schema that validates, reports issues, and provides fallback
 *
 * @example
 * ```ts
 * const schema = v.object({
 *   donorORTimeString: fallbackWithIssue(v.string(), '--:--', '@WARNING:Invalid time string'),
 *   ActiveTrail: fallbackWithIssue(v.array(itemSchema), [], '@WARNING:Invalid trail data'),
 * });
 * ```
 */
export const fallbackWithIssue = <
  TInput,
  TOutput,
  TIssue extends v.BaseIssue<unknown>,
>(
  schema: v.BaseSchema<TInput, TOutput, TIssue>,
  fallback: TOutput,
  message?: string,
): v.BaseSchema<TInput, TOutput, TIssue> =>
  v.pipe(
    schema,
    v.rawCheck(({ dataset, addIssue }) => {
      // If the schema validation already failed, add our custom issue and provide fallback
      if (dataset.typed === false) {
        addIssue({
          message: message || '@WARNING:Validation failed, using fallback',
        });
        // Create a new dataset with the fallback value
        return {
          typed: true,
          value: fallback,
        };
      }
      return dataset;
    }),
  ) as v.BaseSchema<TInput, TOutput, TIssue>;
